import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, matchPath } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CookieProvider } from "@/contexts/CookieContext";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import ScrollToTop from "@/components/common/ScrollToTop";
import SmoothScroll from "@/components/common/SmoothScroll";
import RevealRoot from "@/components/common/RevealRoot";
import RouteTransition from "@/components/common/RouteTransition";
import GATracker from "@/components/common/GATracker";
import { SpeedInsights } from "@vercel/speed-insights/react";
import "@/lib/webVitals"; // Initialize Web Vitals monitoring
import { lazyPage } from "@/lib/lazyPage";

// Lazy load non-critical UI components to reduce initial bundle
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieConsent = lazy(() => import("@/components/cookies/CookieConsent").then(m => ({ default: m.CookieConsent })));
const Analytics = lazy(() => import("@vercel/analytics/react").then(m => ({ default: m.Analytics })));

// Route pages are code-split. `lazyPage` lets main.tsx load the current
// route's chunk before the first render (see src/lib/lazyPage.tsx).
const Index = lazyPage(() => import("./pages/Index"));
const Services = lazyPage(() => import("./pages/Services"));
const Portfolio = lazyPage(() => import("./pages/Portfolio"));
const Articles = lazyPage(() => import("./pages/Articles"));
const ArticleDetail = lazyPage(() => import("./pages/ArticleDetail"));
const About = lazyPage(() => import("./pages/About"));
const Contact = lazyPage(() => import("./pages/Contact"));
const Book = lazyPage(() => import("./pages/Book"));
const ServicePage = lazyPage(() => import("./pages/ServicePage"));
const PrivacyPolicy = lazyPage(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyPage(() => import("./pages/TermsOfService"));
const NotFound = lazyPage(() => import("./pages/NotFound"));

// English routes; every one is also served under the /nl/ prefix.
const pageRoutes = [
  { path: "/", page: Index },
  { path: "/services", page: Services },
  { path: "/services/:serviceId", page: ServicePage },
  { path: "/cases", page: Portfolio },
  { path: "/articles", page: Articles },
  { path: "/articles/:slug", page: ArticleDetail },
  { path: "/about", page: About },
  { path: "/contact", page: Contact },
  { path: "/book", page: Book },
  { path: "/privacy-policy", page: PrivacyPolicy },
  { path: "/terms-of-service", page: TermsOfService },
];

const routePaths = pageRoutes.flatMap((route) => [
  { path: route.path, page: route.page },
  { path: route.path === "/" ? "/nl" : `/nl${route.path}`, page: route.page },
]);

/** Load the chunk for `pathname` so the first render does not suspend. */
export const preloadRoute = (pathname: string) => {
  const match = routePaths.find(({ path }) => matchPath(path, pathname));
  return (match?.page ?? NotFound).preload();
};

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CookieProvider>
          <TooltipProvider>
            <BrowserRouter>
              <LanguageProvider>
                <SmoothScroll />
                <ScrollToTop />
                <RouteTransition preload={preloadRoute} />
                <GATracker />
                <Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
                  </div>
                }>
                <Routes>
                  {routePaths.map(({ path, page: Page }) => (
                    <Route key={path} path={path} element={<Page />} />
                  ))}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                <RevealRoot />
                {/* Lazy loaded non-critical components */}
                <Suspense fallback={null}>
                  <Toaster />
                  <Sonner />
                  <CookieConsent />
                  <Analytics />
                  <SpeedInsights />
                </Suspense>
              </LanguageProvider>
              </BrowserRouter>
            </TooltipProvider>
        </CookieProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
